import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { RocketIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { RadioList, RadioListItem } from '../../../components/ui/button-radio-list.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { CreateNewDraftLink } from '../../sales-draft/components/create-new-draft-link.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';

type QuoteOrderReleaseDialogProps = {
    transactionId: string;
    type: SrSalesOrderType;
};

export const QuoteOrderReleaseDialog = DialogStateManager.create(
    ({ transactionId, type }: QuoteOrderReleaseDialogProps) => {
        const { remove } = useDialog();
        const { t } = useLocale('quotes-orders-actions');
        const { toast } = useToast();
        const { canSendSalesOrderPdfByEmail } = useAppConfig();
        const [sendEmail, setSendEmail] = useState(canSendSalesOrderPdfByEmail ? '1' : '0');

        const { refetch, data } = useDetailQuoteOrder({ transactionId });

        const { mutate: release, isLoading } = api.platoUser.command.salesOrder.order.release.useMutation({
            onSuccess() {
                toast({
                    icon: 'success',
                    title: t(`${type}__release_toast__success_title`),
                    description: (
                        <Trans
                            t={t}
                            i18nKey={`${type}__release_toast__success_description`}
                            components={{
                                linkTag: <CreateNewDraftLink customerId={data?.header.customer_id} />,
                            }}
                        />
                    ),
                });

                refetch();
                remove();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleRelease = () => {
            release({
                sales_order_id: transactionId,
                should_send_email: Boolean(Number(sendEmail)),
            });
        };

        return (
            <BaseConfirmationDialog
                icon={RocketIcon}
                title={t(`${type}__release_confirmation__title`)}
                description={t(`${type}__release_confirmation__description`)}
            >
                <div className="flex w-full">
                    <RadioList defaultValue={sendEmail} onValueChange={setSendEmail}>
                        <RadioListItem
                            disabled={!canSendSalesOrderPdfByEmail}
                            value="1"
                            id="1"
                            label={t('send_pdf_by_email')}
                        />
                        <RadioListItem value="0" id="0" label={t('do_not_send_pdf_by_email')} />
                    </RadioList>
                </div>
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={remove}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleRelease} isLoading={isLoading}>
                        {t('form__release', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
